import { computed, Ref, unref } from '@nuxtjs/composition-api';
import { Logger, sharedRef, useVSFContext } from '@vue-storefront/core';
import { AddCartEntryProps, GetCartEntryProps, GetMaxProductQuantityProps, RemoveCartEntryProps, UpdateCartEntryProps, UseCartEntriesComposable, UseCartEntriesError, UseCartEntriesLoading } from '../types';
import { useCart } from '../useCart';
import * as actions from './actions';

export const useCartEntries = (id?: string): UseCartEntriesComposable => {
  const context = useVSFContext();
  const { cart, set: setCart } = useCart(id);
  const loading: Ref<UseCartEntriesLoading> = sharedRef({
    add: false,
    remove: false,
    update: false
  }, `useCartEntries-loading-${id}`);
  const error: Ref<UseCartEntriesError> = sharedRef({
    add: null,
    remove: null,
    update: null
  }, `useCartEntries-error-${id}`);

  const methodController = async (
    methodName: string,
    callback: () => Promise<void>
  ) => {
    Logger.debug(`useCartEntries-${id}.${methodName}`);

    try {
      loading.value[methodName] = true;
      await callback();
      error.value[methodName] = null;
    } catch (err) {
      error.value[methodName] = err;
      Logger.error(`useCartEntries-${id}.${methodName}`, err);
    } finally {
      loading.value[methodName] = false;
    }
  };

  const get = (props: GetCartEntryProps) =>
    actions.getCartEntry(props, unref(cart));

  const getMaxQuantity = (props: GetMaxProductQuantityProps) =>
    actions.getMaxProductQuantity(props, unref(cart));

  const add = async (props: AddCartEntryProps) => {
    await methodController('add', async () => {
      const updatedCart = await actions.addCartEntry(context, props, unref(cart));
      setCart(updatedCart);
    });
  };

  const remove = async (props: RemoveCartEntryProps) => {
    await methodController('remove', async () => {
      const updatedCart = await actions.removeCartEntry(context, props, unref(cart));
      setCart(updatedCart);
    });
  };

  const update = async (props: UpdateCartEntryProps) => {
    await methodController('update', async () => {
      const updatedCart = await actions.updateCartEntry(context, props, unref(cart));
      setCart(updatedCart);
    });
  };

  return {
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    get,
    getMaxQuantity,
    add,
    remove,
    update
  };
};
